import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

import { DataTable, Badge } from '../../../components/nextgen';
import Modal from '../../../components/Shell/Modal';
import {
	TemplateBoolean,
	TemplateDropdown,
	TemplateField,
	TemplateReactSelect,
} from '../../../components/Attributes/Attributes_old';

import Button from '../../../components/Button';
import useModal from '../../../hooks/useModal';
import usePrevious from '../../../hooks/usePrevious';
import useAsync from '../../../hooks/useAsync';

import {
	addCategoryField,
	deleteCategory,
	removeCategoryField,
	updateCategory,
} from '../../../api/categories';

const Categories = ({ module, onRefresh }) => {
	const [form, setForm] = useState({});
	const [isAddFieldOpen, setAddFieldOpen] = useState(false);
	const [isAddToCategoryOpen, setAddToCategoryOpen] = useState(false);
	const [isRemoveFieldOpen, setRemoveFieldOpen] = useState(false);

	const { execute: onDeleteCategory } = useAsync(deleteCategory, {
		immediate: false,
	});

	const { execute: onAddField } = useAsync(addCategoryField, {
		immediate: false,
	});

	const { execute: onRemoveField } = useAsync(removeCategoryField, {
		immediate: false,
	});

	const { execute: onUpdateCategory } = useAsync(updateCategory, {
		immediate: false,
	});

	const { open: addCategoryOpen, setOpen: setAddCategoryOpen } =
		useModal('addCategoryModal');

	const availableFields = module?.attribute_fields?.filter(
		(field) => !field?.isActive,
	);

	const prevCategoryOpen = usePrevious(addCategoryOpen);

	useEffect(() => {
		if (!addCategoryOpen && prevCategoryOpen) {
			onRefresh({ id: module?.id }, null, { silent: true });
		}
	});

	const [deleteConfirm, setDeleteConfirm] = useState(null);

	const sortedCategories =
		module?.categories?.sort((a, b) => a.name.localeCompare(b.name)) || [];

	return (
		<>
			<DataTable
				data={sortedCategories}
				keyField="id"
				columns={[
					{
						key: 'name',
						header: 'Name',
						width: '200px',
						render: (category) => category?.name || null,
					},
					module?.key === 'default:ticket' && {
						key: 'tag',
						header: 'Tag',
						width: '120px',
						sortable: false,
						render: (category) => (
							<input
								type="text"
								defaultValue={category?.tag || ''}
								placeholder="TAG"
								maxLength={5}
								className="w-20 px-2 py-1 text-xs font-mono uppercase border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
								onBlur={(e) => {
									const newTag = e.target.value.toUpperCase();
									if (newTag !== category?.tag) {
										onUpdateCategory({ id: category?.id, tag: newTag }, () =>
											onRefresh({ id: module?.id }, null, { silent: true }),
										);
									}
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.target.blur();
									}
								}}
							/>
						),
					},
					false && {
						key: 'attribute_fields',
						header: 'Custom Fields',
						sortable: false,
						render: (category) => (
							<div className="flex flex-wrap gap-1">
								{category?.attribute_fields?.map((field) => (
									<Badge
										key={field?.id}
										variant="default"
										className={field?.isDefault ? '' : 'cursor-pointer'}
										onClick={() =>
											!field?.isDefault
												? setRemoveFieldOpen({
														categoryId: category?.id,
														fieldId: field?.id,
													})
												: {}
										}
									>
										{field?.label}
									</Badge>
								))}
								<Badge
									variant="default"
									className="cursor-pointer"
									onClick={() =>
										setAddToCategoryOpen({
											id: category?.id,
											skip: category?.attribute_fields?.map(
												(field) => field.id,
											),
										})
									}
								>
									<PlusIcon className="w-3 h-3 text-gray-600" />{' '}
									{category?.attribute_fields?.length ? '' : 'Add Field'}
								</Badge>
							</div>
						),
					},
				].filter(Boolean)}
				actions={
					<Button onClick={() => setAddCategoryOpen({ moduleId: module?.id })}>
						Create Category
					</Button>
				}
				rowActions={(category) =>
					!category?.isDefault ? (
						<button
							onClick={() => setDeleteConfirm(category)}
							className="p-1 text-gray-400 hover:text-red-500 transition-colors"
							title="Delete category"
						>
							<TrashIcon className="w-4 h-4" />
						</button>
					) : null
				}
				emptyTitle="No categories found"
				emptyDescription="Create a category to get started"
				containerClassName="border border-gray-200 rounded-lg"
			/>

			{/* Delete Confirmation Modal */}
			<Modal
				title="Delete Category"
				open={!!deleteConfirm}
				onClose={() => setDeleteConfirm(null)}
			>
				<p className="text-sm text-gray-600 mb-4">
					Are you sure you want to delete this category?
				</p>
				<div className="flex justify-end gap-2">
					<Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
						Cancel
					</Button>
					<Button
						variant="danger"
						onClick={() => {
							onDeleteCategory(
								{
									moduleId: module?.id,
									categoryId: deleteConfirm?.id,
								},
								() => {
									onRefresh({ id: module?.id }, null, { silent: true });
									setDeleteConfirm(null);
								},
							);
						}}
					>
						Delete
					</Button>
				</div>
			</Modal>

			<Modal
				title="Are you sure you want to remove this field?"
				open={isRemoveFieldOpen}
				onClose={() => setRemoveFieldOpen(false)}
			>
				<div className="flex justify-end">
					<Button
						onClick={() => {
							const categoryId = isRemoveFieldOpen?.categoryId;
							const fieldId = isRemoveFieldOpen?.fieldId;

							onRemoveField(
								{
									id: categoryId,
									fieldId,
								},
								() => {
									onRefresh({ id: module?.id }, null, { silent: true });
									setRemoveFieldOpen(false);
								},
							);
						}}
					>
						Remove
					</Button>
				</div>
			</Modal>

			<Modal
				title="Add field to category"
				open={isAddToCategoryOpen}
				onClose={() => setAddToCategoryOpen(false)}
			>
				<TemplateReactSelect
					options={availableFields?.map((field) => ({
						label: field?.label,
						value: field?.id,
					}))}
					onChange={(index, value) => {
						const categoryId = isAddToCategoryOpen?.id;
						const fieldId = value?.value;
						onAddField(
							{
								id: categoryId,
								fieldId,
							},
							() => {
								onRefresh({ id: module?.id }, null, { silent: true });
								setAddToCategoryOpen(false);
							},
						);
					}}
					skip={isAddToCategoryOpen?.skip}
				/>
			</Modal>

			<Modal
				title="Create a new field"
				open={isAddFieldOpen}
				onClose={() => setAddFieldOpen(false)}
			>
				<div className="py-3 space-y-4">
					<TemplateField
						name="Label"
						onChange={(dataIndex, value) =>
							setForm({
								...form,
								label: value,
								key: value.replace(/\s+/g, '-').toLowerCase(),
							})
						}
						value={form?.label}
					/>
					<TemplateField name="Key" disabled value={form?.key} />
					<TemplateDropdown
						name="Field Type"
						options={[{ label: 'Text', value: 'text' }]}
						placeholder="Select a field type"
					/>
					<TemplateBoolean name="Is searchable" />
					<TemplateBoolean name="Is required" />

					<div className="flex justify-end mt-3">
						<Button>Add Field</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default Categories;
